/**
 * SubscriptionBanner.js
 *
 * Shows a top-of-page banner for:
 *   - Trials expiring in ≤ 7 days
 *   - Expired trials
 *   - Past-due payments
 *   - Suspended orgs
 *
 * Hidden for: active subscriptions, super_admin viewing their own org,
 * and dismissed banners (per session).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, X, Clock, CreditCard, ShieldOff } from 'lucide-react';

export default function SubscriptionBanner() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !user) return null;

  const org    = user.organization || {};
  const status = org.subscription_status;
  const plan   = org.subscription_plan;
  const active = org.is_active !== false;

  // Compute trial days left
  const trialDaysLeft = (() => {
    if (!org.trial_ends_at) return null;
    return Math.ceil((new Date(org.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24));
  })();

  // Early bird plans are sponsored — never show a subscription banner
  if (plan === 'early_bird') return null;

  const isTrial = plan === 'trial' || plan === 'trialing' || status === 'trialing';
  const trialExpired = isTrial && trialDaysLeft !== null && trialDaysLeft <= 0;

  // Determine which banner to show (priority order)
  let banner = null;

  if (!active) {
    banner = {
      icon:    <ShieldOff size={15} />,
      color:   '#ef4444',
      bg:      'rgba(239,68,68,0.12)',
      border:  'rgba(239,68,68,0.3)',
      message: 'Your firm account has been suspended. Contact support to restore access.',
      cta:     null,
      dismiss: false,
    };
  } else if (trialExpired) {
    banner = {
      icon:    <AlertTriangle size={15} />,
      color:   '#ef4444',
      bg:      'rgba(239,68,68,0.1)',
      border:  'rgba(239,68,68,0.25)',
      message: 'Your free trial has expired.',
      cta:     { label: 'Upgrade Now', path: '/upgrade' },
      dismiss: false,
    };
  } else if (isTrial && trialDaysLeft !== null && trialDaysLeft <= 7) {
    banner = {
      icon:    <Clock size={15} />,
      color:   '#f59e0b',
      bg:      'rgba(245,158,11,0.08)',
      border:  'rgba(245,158,11,0.25)',
      message: trialDaysLeft <= 1
        ? 'Your free trial expires today!'
        : `Your free trial expires in ${trialDaysLeft} days.`,
      cta:     { label: 'View Plans', path: '/upgrade' },
      dismiss: true,
    };
  } else if (status === 'past_due') {
    banner = {
      icon:    <CreditCard size={15} />,
      color:   '#ef4444',
      bg:      'rgba(239,68,68,0.1)',
      border:  'rgba(239,68,68,0.25)',
      message: 'Your payment is past due. Writes are disabled until payment is resolved.',
      cta:     { label: 'Update Billing', path: '/billing' },
      dismiss: false,
    };
  } else if (status === 'cancelled') {
    banner = {
      icon:    <AlertTriangle size={15} />,
      color:   '#9ca3af',
      bg:      'rgba(156,163,175,0.1)',
      border:  'rgba(156,163,175,0.2)',
      message: 'Your subscription has been cancelled. Reactivate to continue using ZeusCMS.',
      cta:     { label: 'Reactivate', path: '/upgrade' },
      dismiss: false,
    };
  }

  if (!banner) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '9px 16px',
      background: banner.bg,
      borderBottom: `1px solid ${banner.border}`,
      color: banner.color,
      fontSize: '0.82rem',
      fontWeight: 500,
    }}>
      <span style={{ flexShrink: 0 }}>{banner.icon}</span>
      <span style={{ flex: 1, color: 'var(--text-primary)' }}>
        {banner.message}
      </span>
      {banner.cta && (
        <button
          onClick={() => navigate(banner.cta.path)}
          style={{
            background: banner.color, color: '#fff', border: 'none',
            borderRadius: 'var(--radius-sm)', padding: '4px 12px',
            fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {banner.cta.label}
        </button>
      )}
      {banner.dismiss && (
        <button
          onClick={() => setDismissed(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0, padding: '2px' }}
          title="Dismiss"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}
